import { ReactComponent as WaGroupSVG } from '@common-assets/waGroup.svg';
import { chunkIds } from '@common-reducers/in-query';
import { ReactComponent as WaPersonSVG } from '@common-assets/waPerson.svg';
import { MessageLog } from '@common-models/message-log';
import { Lightbulb, RefreshCw } from 'lucide-react';
import moment from 'moment';
import { MessageLogCard } from '@/common/views/user-overview/tabs/message-logs/components/MessageLogCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DBMessageLogThunks, messageLogSelectByQuery } from '@common-reducers/DBServiceThunks';
import { useAppSelector, useAppDispatch } from '@common-reducers/hooks/store.hook';
import { memo, useEffect, useState, useMemo } from 'react';
import { selectShouldShowRetry, selectMessageIdsByContactAndCampaign, selectContactIdFromMessageLog } from '@common-reducers/CampaignSelectors';
import { Loader2 } from 'lucide-react';
import { MessageStatusWithError } from '@/common/views/user-overview/tabs/message-logs/components/MessageStatusWithError';
import { MessageLogStatusBadge } from '@/common/views/user-overview/tabs/message-logs/components/MessageLogStatusBadge';
import ContactAvatar from '../bulk-dashboard/contact-table/contact-avatar';
import { PhoneDisplay } from '@common-components/tasks/columns-cells/phone-number-cell/phone-display';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TooltipContainerProvider } from '@/common/contexts/TooltipContainerContext';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { AlertCircle } from 'lucide-react';
import 'react-phone-input-2/lib/style.css';
import { isExtensionContext } from '@common-services/detect-context';
import { retrySendMessageThunk } from '@extension/context/content/thunks/WhatsappSendMessageThunk';
import { useRetryMessageMutation } from '@common-reducers/backend-api/backend-api';
import { userMessagesSelectOneObjectByQuery } from '@common-reducers/DBServiceThunks';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { isNotOnWhatsAppFailure } from '@common-components/message-status/message-status-config';


interface CampaignMessageLogRowProps {
    profilePicUrl?: string;
    isGroup?: boolean;
    contactName?: string;
    contactPhone?: string;
    messageLog: MessageLog;
    onClick?: () => void;
    index: number;
    status: string;
}

// Stable empty results for the gated selectors below — returning a fresh []
// from useAppSelector would fail reference equality and re-render on every
// store update.
const EMPTY_MESSAGE_LOGS: MessageLog[] = [];
const EMPTY_MESSAGE_IDS: string[] = [];

// Hoisted from the component body: defining these inside render created new
// component types every render, forcing React to unmount/remount their whole
// subtree instead of reconciling it.
interface StatusFieldProps {
    messageLog: MessageLog;
    status: string;
    shouldShowRetry: boolean;
    isRetrying: boolean;
    onRetry: (e: React.MouseEvent) => void;
    hasHistory: boolean;
    onOpenHistory: () => void;
    /** BE#710 — the recipient-not-on-WhatsApp failure variant (surfaced in the error tooltip). */
    isNotOnWhatsApp: boolean;
}

const StatusField = ({ messageLog, status, shouldShowRetry, isRetrying, onRetry, hasHistory, onOpenHistory, isNotOnWhatsApp }: StatusFieldProps) => {
    const { t } = useTranslation(namespace);
    const dateTime = messageLog?.message?.createdAt ? moment(new Date(messageLog?.message?.createdAt)) : moment(messageLog?.createdAt ? new Date(messageLog.createdAt) : new Date());
    const date = dateTime.format('DD/MM/YYYY');
    const time = dateTime.format('HH:mm:ss');

    // Check if message has error (for waiting messages that might not have messageId)
    const hasError = (messageLog as any)?.error || (messageLog as any)?.sendFailureError;

    return (
        <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
                <div className="flex flex-col items-end min-w-[70px]">
                    <span className="text-xs leading-tight text-slate-600 dark:text-slate-400">
                        {date}
                    </span>
                    <span className="text-xs leading-tight opacity-80 text-slate-600 dark:text-slate-400">
                        {time}
                    </span>
                </div>
                {messageLog?.messageId ? (
                    <MessageStatusWithError messageId={messageLog.messageId} />
                ) : (
                    <div className="flex items-center gap-2">
                        <MessageLogStatusBadge status={status as any} />
                        {hasError && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="cursor-help">
                                        <AlertCircle className="h-4 w-4 text-red-600" />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p className="text-xs">
                                        {isNotOnWhatsApp
                                            ? t('messageStatus.failedNotOnWhatsApp.description')
                                            : t('bulkDashboard.errorOccurred')}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                )}
                {/* BE#710 (conductor ruling): a not-on-WhatsApp failure is terminal — a resend can
                    never succeed — so the per-row Retry action is hidden for it (it is also excluded
                    from "Retry All"; see selectMessagesToRetry). A plain failed row keeps Retry. */}
                {(shouldShowRetry || status === "Waiting In DB") && !isNotOnWhatsApp && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={onRetry}
                                disabled={isRetrying}
                            >
                                {isRetrying ? (
                                    <Loader2 className="h-4 w-4 text-slate-500 dark:text-slate-400 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('bulkDashboard.retrySendingMessage')}</p>
                        </TooltipContent>
                    </Tooltip>
                )}
                {hasHistory && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenHistory();
                                }}
                            >
                                <Lightbulb className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('bulkDashboard.viewStatusHistory')}</p>
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

const ContactInfo = ({
    isGroup,
    contactName,
    contactPhone,
    messageLog,
    messageCount
}: Partial<CampaignMessageLogRowProps> & { messageCount?: number }) => {
    const { t } = useTranslation(namespace);
    const contactId = messageLog?.message?.contactList?.[0]?.id ?? (messageLog as any)?.contactList?.[0]?.id
    return (
        <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
                {contactId ? (
                    <ContactAvatar
                        contactId={contactId}
                        size={40}
                        sx={{
                            justifyContent: 'flex-start',
                        }}
                    />
                ) : isGroup ? (
                    <WaGroupSVG />
                ) : (
                    <WaPersonSVG />
                )}
            </div>

            <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate text-slate-900 dark:text-slate-100">
                        {contactName}
                    </span>
                    {messageCount && messageCount > 1 && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 cursor-help">
                                    {messageCount}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t('bulkDashboard.messagesForContact', { count: messageCount })}</p>
                            </TooltipContent>
                        </Tooltip>
                    )}
                    {!isGroup && contactPhone && (
                        <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200">
                            <PhoneDisplay phone={contactPhone} />
                        </div>
                    )}
                    {isGroup && contactPhone && (
                        <span className="text-xs px-1.5 py-0.5 rounded opacity-60 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                            {contactPhone}
                        </span>
                    )}
                </div>
                {(() => {
                    // Handle both message log structure (message.message) and direct UserMessage structure (message as string)
                    const messageText = typeof messageLog?.message === 'string'
                        ? messageLog.message
                        : messageLog?.message?.message;
                    return messageText ? (
                        <span className="text-sm truncate text-slate-600 dark:text-slate-400">
                            {messageText}
                        </span>
                    ) : null;
                })()}
            </div>
        </div>
    )
};

// Main component
function CampaignMessageLogRow({
    status,
    profilePicUrl,
    isGroup,
    contactName,
    contactPhone,
    messageLog,
    onClick,
    index,
}: CampaignMessageLogRowProps) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
    const [isLoadingMessageLogs, setIsLoadingMessageLogs] = useState(false);
    
    // Get portal container for TooltipContainerProvider (DialogContent gets it automatically from context)
    const tooltipContainer = usePortalContainer();

    // Check if message is failed (has error status or error field)
    const isFailed = status === 'failed' || messageLog.status === 'failed' || (messageLog as any)?.error || (messageLog as any)?.sendFailureError;
    
    // Use selector to determine if retry button should be shown
    const shouldShowRetry = useAppSelector((state) => 
        selectShouldShowRetry(state, messageLog?.messageId)
    );

    // Get the UserMessage from Redux if we have messageId
    // For "Waiting In DB" messages, messageLog itself might be a UserMessage object
    const userMessage = useAppSelector((state) => {
        // If messageLog has _id and no messageId, it's likely a UserMessage object itself
        if ((messageLog as any)?._id && !messageLog?.messageId) {
            return messageLog as unknown as UserMessage;
        }
        // Otherwise, try to get it from Redux using messageId
        return messageLog?.messageId ? userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, {
            _id: messageLog.messageId
        }) : null;
    }) as UserMessage | null;

    // BE#710: recognise a not-on-WhatsApp failure with the SAME resolution the status filter and the
    // status badge use — full store doc → delivery-evidence (carries the failure reason after BE#710)
    // → the log's EMBEDDED snapshot (where sendFailureReason actually lives) → the log itself. The
    // naive `userMessage ?? (messageLog as UserMessage)` missed it: the log view never loads
    // userMessages into the store (userMessage is null) and the reason is on messageLog.message, not
    // the top-level log — so the row failed to recognise the terminal failure and kept offering Retry.
    const isNotOnWhatsApp = useAppSelector((state) => {
        const mid = messageLog?.messageId;
        const resolved = (mid ? (userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, { _id: mid }) as UserMessage | undefined) : undefined)
            ?? (mid ? (state.CampaignReducer.deliveryEvidence?.[mid] as UserMessage | undefined) : undefined)
            ?? ((messageLog as { message?: UserMessage })?.message)
            ?? (messageLog as unknown as UserMessage);
        return isNotOnWhatsAppFailure(resolved ?? null);
    });

    // RTK Query mutation for retry (non-extension context)
    const [retryMessage, { isLoading: isRetrying }] = useRetryMessageMutation();

    // Handle retry
    const handleRetry = async (e: React.MouseEvent) => {
        e.stopPropagation();
        
        // For "Waiting In DB" messages, messageLog is actually a UserMessage object with _id
        // For regular messages, messageLog has messageId
        const messageId = messageLog?.messageId || (messageLog as any)?._id;
        
        if (!messageId) {
            console.error('Cannot retry: messageId is missing');
            return;
        }

        try {
            const isExtension = isExtensionContext();
            
            if (isExtension && userMessage) {
                // In extension context, use retrySendMessageThunk
                await dispatch(retrySendMessageThunk({ 
                    userMessage, 
                    retryTrigger: 'manual' 
                }));
            } else {
                // In non-extension context (website), use RTK Query mutation
                await retryMessage({
                    messageId: messageId
                }).unwrap();
            }
        } catch (error) {
            console.error('Error retrying message:', error);
        }
    };

    // Get contact ID using selector
    const contactId = useAppSelector((state) => 
        selectContactIdFromMessageLog(state, messageLog)
    );
    
    const campaignId = messageLog?.campaignId || messageLog?.message?.campaignId;

    // Get all messageIds for this contact in the campaign
    const allMessageIdsForContact = useAppSelector((state) => {
        if (!campaignId || !contactId) return EMPTY_MESSAGE_IDS;
        const campaignIdStr = typeof campaignId === 'string' ? campaignId : String(campaignId);
        return selectMessageIdsByContactAndCampaign(state, campaignIdStr, contactId);
    });

    // Determine if there are multiple messages for this contact
    const hasMultipleMessages = allMessageIdsForContact.length > 1;

    // Get message logs for all messageIds of this contact.
    // Gated behind the history dialog: the $in re-query is only needed while
    // the dialog is open, and running it per row on every store update was a
    // significant per-render cost.
    const messageLogs = useAppSelector((state) => {
        if (!isHistoryDialogOpen || !allMessageIdsForContact || allMessageIdsForContact.length === 0) {
            return EMPTY_MESSAGE_LOGS;
        }

        // Query with $in operator (works fine for single element arrays too)
        const query: any = {
            messageId: { $in: allMessageIdsForContact },
            $or: [
                { deleted: { $exists: false } },
                { deleted: false }
            ]
        };

        return messageLogSelectByQuery(state.DBMessageLogReducer, query);
    });

    // Fetch message logs when dialog opens
    useEffect(() => {
        if (isHistoryDialogOpen && allMessageIdsForContact.length > 0 && !isLoadingMessageLogs) {
            setIsLoadingMessageLogs(true);
            
            // Fetch logs for all messageIds, in <=20-id batches (#803: longer `$in` 500s on the backend's qs arrayLimit).
            Promise.allSettled(chunkIds(allMessageIdsForContact).map((batch) =>
                dispatch(DBMessageLogThunks.find({
                    messageId: { $in: batch },
                    $sort: { createdAt: -1 },
                    $limit: -1,
                } as any)),
            ))
            .finally(() => {
                setIsLoadingMessageLogs(false);
            });
        } else if (!isHistoryDialogOpen) {
            setIsLoadingMessageLogs(false);
        }
    }, [isHistoryDialogOpen, allMessageIdsForContact, dispatch]);
    // Compute MessageLogRow from fetched logs
    const messageLogRow = useMemo(() => {
        if (!messageLogs || messageLogs.length === 0 || allMessageIdsForContact.length === 0) {
            return null;
        }

        const logs = messageLogs;
        
        // Sort logs by createdAt descending to get latest
        const sortedLogs = [...logs].sort((a, b) => {
            const timeA = new Date(a.createdAt || 0).getTime();
            const timeB = new Date(b.createdAt || 0).getTime();
            return timeB - timeA;
        });
        const latestLog = sortedLogs[0];

        // Determine latest status
        const latestStatus = latestLog.status as any;

        // Check if any log has an error
        const hasError = logs.some(log => log.status === 'failed' || log.error);

        // Group logs by attemptType and then by time proximity (10 seconds)
        const logsByAttemptType = logs.reduce((acc: Record<string, typeof logs>, log) => {
            const attemptType = log.metadata?.attemptType || 'initial';
            if (!acc[attemptType]) {
                acc[attemptType] = [];
            }
            acc[attemptType].push(log);
            return acc;
        }, {});

        // Group logs within each attemptType by time proximity (10 seconds)
        const groupedLogs: any[][] = [];
        Object.values(logsByAttemptType).forEach(attemptLogs => {
            // Sort by createdAt
            const sortedAttemptLogs = [...attemptLogs].sort((a, b) => {
                const timeA = new Date(a.createdAt || 0).getTime();
                const timeB = new Date(b.createdAt || 0).getTime();
                return timeA - timeB;
            });

            // Group by 10-second intervals
            let currentGroup: any[] = [];
            let lastTime = 0;
            const TEN_SECONDS = 10000;

            sortedAttemptLogs.forEach(log => {
                const logTime = new Date(log.createdAt || 0).getTime();
                if (currentGroup.length === 0 || (logTime - lastTime) <= TEN_SECONDS) {
                    currentGroup.push(log);
                } else {
                    groupedLogs.push(currentGroup);
                    currentGroup = [log];
                }
                lastTime = logTime;
            });
            
            if (currentGroup.length > 0) {
                groupedLogs.push(currentGroup);
            }
        });

        const row: any = {
            id: allMessageIdsForContact.join('-'), // Use contact-based ID
            messageId: allMessageIdsForContact[0], // Primary messageId for compatibility
            message: messageLog.message,
            latestStatus,
            hasError,
            logCount: logs.length,
            latestUpdate: latestLog.createdAt || messageLog.createdAt,
            isMainRow: true,
            dueDate: messageLog.message?.dueDate,
            logs: groupedLogs
        };

        return row;
    }, [messageLogs, messageLog, allMessageIdsForContact]);

    if (!messageLog) {
        return (
            <div className="flex items-center p-3 min-h-[72px] border-b border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-600 dark:text-slate-300">{t('bulkDashboard.invalidMessageData')}</span>
            </div>
        );
    }

    return (
        <>
            <div
                key={index}
                className={cn(
                    "flex items-center justify-between p-3 min-h-[72px] transition-colors",
                    "bg-white dark:bg-slate-800",
                    "border-b border-slate-200 dark:border-slate-700",
                    "text-slate-900 dark:text-slate-100"
                )}
            >
                <ContactInfo
                    isGroup={isGroup}
                    contactName={contactName}
                    contactPhone={contactPhone}
                    messageLog={messageLog}
                    messageCount={hasMultipleMessages ? allMessageIdsForContact.length : undefined}
                />
                <StatusField
                    status={status}
                    messageLog={messageLog}
                    shouldShowRetry={shouldShowRetry}
                    isRetrying={isRetrying}
                    onRetry={handleRetry}
                    hasHistory={allMessageIdsForContact.length > 0}
                    onOpenHistory={() => setIsHistoryDialogOpen(true)}
                    isNotOnWhatsApp={isNotOnWhatsApp}
                />
            </div>
            {/* Message Log Dialog */}
            <Dialog 
                open={isHistoryDialogOpen} 
                onOpenChange={(open) => setIsHistoryDialogOpen(open)}
            >
                <DialogContent 
                    className="max-w-4xl w-full h-[90vh] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
                    style={{
                        backgroundColor: 'var(--color-bg)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        height: '90vh',
                        maxHeight: '90vh'
                    }}
                >
                    <style>{`
                        [data-radix-dialog-content] button[class*="absolute right-4 top-4"] {
                            display: none !important;
                        }
                    `}</style>
                    <TooltipContainerProvider container={tooltipContainer}>
                        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-solid border-[var(--color-border)] sticky top-0 bg-[var(--color-bg)] pr-12 relative" style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
                            <DialogTitle>
                                {t('bulkDashboard.messageLogs')}
                                {hasMultipleMessages && (
                                    <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                                        ({t('bulkDashboard.messagesForContact', { count: allMessageIdsForContact.length })})
                                    </span>
                                )}
                            </DialogTitle>
                            <DialogDescription>
                                {hasMultipleMessages
                                    ? t('bulkDashboard.detailedTimelineMultiple', { count: allMessageIdsForContact.length })
                                    : t('bulkDashboard.detailedTimeline')
                                }
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4 min-h-0" style={{ scrollPaddingTop: '0', marginTop: 0 }}>
                            {isLoadingMessageLogs ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-10 w-10 animate-spin text-[var(--color-primary)]" />
                                </div>
                            ) : allMessageIdsForContact.length > 0 ? (
                                <MessageLogCard messageId={allMessageIdsForContact} />
                            ) : (
                                <div className="text-center py-8 text-slate-600 dark:text-slate-300">
                                    {t('bulkDashboard.noMessageLogsFound')}
                                </div>
                            )}
                        </div>
                    </TooltipContainerProvider>
                </DialogContent>
            </Dialog>
        </>
    );
}

// Memoized: rendered per row inside virtualized campaign log lists.
export default memo(CampaignMessageLogRow);
